import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ReorderableItemListProps<T> {
  items: T[];
  /** React key — stable per item across renders. Caller usually combines
   *  the item's id field with index since the same id may appear twice
   *  during reorder transitions. */
  getKey: (item: T, idx: number) => string;
  /** Per-row content rendered between the index number and the action
   *  buttons. Caller decides thumbnail/title/subtitle layout — the shell
   *  only owns the structural chrome (index, up/down/remove). */
  renderItem: (item: T, idx: number) => ReactNode;
  /** Fired on reorder or remove with the next array. Reorder + delete
   *  logic stays inside the component so the two manual editors don't
   *  re-implement the array math. */
  onChange: (nextItems: T[]) => void;
  /** Hard cap — used in the count header + applied to the default Add
   *  button. When `addSlot` is provided, the caller is responsible for
   *  enforcing the cap inside their custom add UI. */
  maxCount: number;
  /** Add button label (e.g. "เพิ่มบทความ"). Ignored when `addSlot` is
   *  provided. */
  addLabel?: string;
  /** Default add handler — fires on the single built-in Add button.
   *  Ignored when `addSlot` is provided. */
  onAddClick?: () => void;
  /** Custom add-zone JSX, replaces the single Add button when present.
   *  Use this for multi-type add toolbars (e.g. the customized editor's
   *  four-button row, one button per itemType). */
  addSlot?: ReactNode;
  /** Empty-state copy shown when items.length === 0. */
  emptyText: string;
  /** Unit shown after the count header ("5 / 10 รายการ"). Defaults to
   *  "รายการ" to match the pre-refactor copy. */
  countUnit?: string;
}

/**
 * Shared shell for an admin-curated, ordered list of items with up/down/
 * remove + an Add button that opens a picker dialog (owned by the caller).
 *
 * Used by ConfigEditorArticleManual + ConfigEditorVideoManual. Both used
 * to ship near-identical inline copies of this UI; this extraction means
 * a future manual-mode type (event, photo, …) gets the shell for free.
 *
 * What stays in the caller: title/thumb hydration cache, picker dialog,
 * and the actual <PickerDialog> JSX (each type has a different picker).
 */
export function ReorderableItemList<T>({
  items,
  getKey,
  renderItem,
  onChange,
  maxCount,
  addLabel,
  onAddClick,
  addSlot,
  emptyText,
  countUnit = 'รายการ',
}: ReorderableItemListProps<T>) {
  const remaining = maxCount - items.length;

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onChange(next);
  };

  const remove = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-500">
          {items.length} / {maxCount} {countUnit}
        </div>
        {addSlot ?? (
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={remaining <= 0}
            onClick={onAddClick}
          >
            <Plus className="mr-1 h-4 w-4" /> {addLabel}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400 dark:border-slate-700">
          {emptyText}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, idx) => (
            <li
              key={getKey(it, idx)}
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900"
            >
              <span className="w-6 text-center text-xs tabular-nums text-slate-400">{idx + 1}</span>
              <div className="flex min-w-0 flex-1 items-center gap-2">{renderItem(it, idx)}</div>
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  title="เลื่อนขึ้น"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={idx === items.length - 1}
                  onClick={() => move(idx, 1)}
                  title="เลื่อนลง"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(idx)}
                  title="ลบ"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
